import React, { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { HelpCircle, ExternalLink, Loader2, Send, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function HelpRow({ item, onResolve }) {
  const [open, setOpen] = useState(false);
  const [response, setResponse] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [saveAsTip, setSaveAsTip] = useState(false);
  const [tipTitle, setTipTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    await onResolve(item.onboarding_id, item.id, {
      response,
      send_email_to_client: sendEmail,
      save_as_tip: saveAsTip,
      tip_title: tipTitle || undefined,
    });
    setBusy(false);
  };
  const closeQuietly = async () => {
    setBusy(true);
    await onResolve(item.onboarding_id, item.id, { response: "", send_email_to_client: false, save_as_tip: false });
    setBusy(false);
  };

  return (
    <div className="bg-white border border-sky-200 rounded-lg p-4 shadow-sm" data-testid={`help-row-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900">{item.company_name}</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-sky-100 text-sky-700 border border-sky-200">{item.section_title}</span>
            <span className="text-xs text-slate-500">{item.client_name ? `${item.client_name} · ` : ""}{item.client_email}</span>
            <span className="text-xs text-slate-400">· {timeAgo(item.created_at)}</span>
          </div>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap break-words">{item.question}</p>
        </div>
        <Link to={`/admin/onboarding/${item.onboarding_id}`} className="inline-flex items-center gap-1 text-xs text-brand-primary hover:underline shrink-0" data-testid={`help-row-view-${item.id}`}>
          <ExternalLink size={12}/>Open
        </Link>
      </div>
      {open ? (
        <div className="mt-3 space-y-2">
          <Textarea
            value={response}
            onChange={(e) => setResponse(e.target.value)}
            placeholder="Type your reply to the client (optional — leave empty to just mark resolved without an email)"
            rows={3}
            maxLength={5000}
            className="resize-none text-sm"
            data-testid={`help-row-response-${item.id}`}
          />
          <div className="flex flex-col gap-1.5 pt-1">
            <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-sky-600" data-testid={`help-row-sendemail-${item.id}`}/>
              Email reply to <span className="font-medium text-slate-900">{item.client_email}</span>
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" checked={saveAsTip} onChange={(e) => setSaveAsTip(e.target.checked)} className="accent-sky-600" data-testid={`help-row-savetip-${item.id}`}/>
              <span>Save as a section tip — future clients on <span className="font-medium text-slate-900">{item.section_title}</span> will see this Q&amp;A inline.</span>
            </label>
            {saveAsTip && (
              <input
                type="text"
                value={tipTitle}
                onChange={(e) => setTipTitle(e.target.value)}
                placeholder="Tip title (optional — defaults to a snippet of the question)"
                maxLength={160}
                className="ml-6 mt-1 w-full max-w-md text-xs px-2.5 py-1.5 rounded border border-slate-300 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                data-testid={`help-row-tiptitle-${item.id}`}
              />
            )}
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="border-slate-300 text-slate-700 hover:bg-slate-100">Cancel</Button>
            <Button size="sm" onClick={submit} disabled={busy || (sendEmail && !response.trim()) || (saveAsTip && !response.trim())} className="font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid={`help-row-submit-${item.id}`}>
              {busy ? <Loader2 className="animate-spin" size={12}/> : <><Send size={12} className="mr-1"/>{sendEmail ? "Reply & resolve" : "Resolve"}</>}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={() => setOpen(true)} className="font-semibold" style={{ background: "#0F1644", color: "#fff" }} data-testid={`help-row-reply-${item.id}`}>
            <Send size={12} className="mr-1.5"/>Reply
          </Button>
          <Button size="sm" variant="ghost" onClick={closeQuietly} disabled={busy} className="text-slate-600 hover:bg-slate-100" data-testid={`help-row-dismiss-${item.id}`}>
            {busy ? <Loader2 className="animate-spin" size={12}/> : <><CheckCircle2 size={12} className="mr-1"/>Mark resolved</>}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function HelpRequestsPanel({ items, onResolve }) {
  if (!items?.length) return null;
  return (
    <Card className="bg-sky-50 border-sky-200 p-5 mb-6 shadow-sm" data-testid="help-requests-panel">
      <div className="flex items-center gap-2 mb-1">
        <HelpCircle size={16} className="text-sky-700"/>
        <div className="font-medium text-sky-900">Open help requests ({items.length})</div>
      </div>
      <p className="text-xs text-sky-800/70 mb-4">Clients asking for help on specific sections. Reply quickly — fast responses lift completion rates the most.</p>
      <div className="space-y-3">
        {items.map(it => <HelpRow key={it.id} item={it} onResolve={onResolve}/>)}
      </div>
    </Card>
  );
}
